import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { X, Building2, Loader2, CreditCard, CheckCircle2, RefreshCw, Crown } from "lucide-react";
import GdprNotice from "@/components/GdprNotice";
import { useLang } from "@/context/LangContext";

/**
 * RDV 1:1 individuel — exhibitor registration modal.
 * - Members: form is pre-filled from `/portal/my-company` (company name, logo,
 *   description short/long). A "Resynchroniser" button re-pulls the latest
 *   fiche entreprise so the member doesn't have to retype anything.
 * - If event has price_exhibitor > 0 → creates entry as pending_payment + redirects to Stripe Checkout.
 * - Else → creates entry as pending (awaiting admin approval) — except admins which auto-approve.
 */
export default function ExhibitorRegistrationModal({ event, slot, user, onClose, onRegistered }) {
  const { t, lang } = useLang();
  const [form, setForm] = useState({
    company_id: "",
    company_name: user?.company_name || user?.name || "",
    company_logo: "",
    description: "",
    booth_number: "",
    role: slot?.sm_roles?.[0] || "",
  });
  const [loading, setLoading] = useState(false);
  const [resyncing, setResyncing] = useState(false);
  const [myCompany, setMyCompany] = useState(null);
  const touchedRef = useRef(new Set()); // fields the user has edited manually → don't overwrite on resync

  const applyCompanyToForm = (company, { preserveTouched = false } = {}) => {
    if (!company) return;
    const next = {
      company_id: company.id || company._id || "",
      company_name: company.commercial_name || company.name || "",
      company_logo: company.logo || company.logo_url || "",
      description: company.short_description || company.description || "",
    };
    setForm((f) => {
      const out = { ...f };
      Object.entries(next).forEach(([k, v]) => {
        if (preserveTouched && touchedRef.current.has(k)) return;
        if (v) out[k] = v;
      });
      return out;
    });
  };

  // Initial pre-fill from logged-in member's company (silent — no error if non-member)
  useEffect(() => {
    if (!user?.email) return;
    api
      .get("/portal/my-company")
      .then((r) => {
        const c = r.data?.company || null;
        if (c) {
          setMyCompany(c);
          applyCompanyToForm(c);
        }
      })
      .catch(() => { /* non-member or no company — ignore */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email]);

  const resync = async () => {
    setResyncing(true);
    try {
      const r = await api.get("/portal/my-company");
      const c = r.data?.company || null;
      if (!c) {
        toast.error(t("exh_no_company"));
        return;
      }
      setMyCompany(c);
      // Full overwrite on explicit resync — but keep user-edited `booth_number` + `role`
      // (which are event-specific, not part of the company fiche).
      applyCompanyToForm(c, { preserveTouched: false });
      toast.success(t("exh_resynced"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || t("exh_resync_error"));
    } finally {
      setResyncing(false);
    }
  };

  const update = (patch) => {
    Object.keys(patch).forEach((k) => touchedRef.current.add(k));
    setForm((f) => ({ ...f, ...patch }));
  };

  const pricing = event?.pricing || {};
  const isPaid = pricing.is_paid && Number(pricing.price_exhibitor || 0) > 0 && user?.role !== "admin";
  const price = Number(pricing.price_exhibitor || 0);
  const currency = (pricing.currency || "eur").toUpperCase();
  const fmtPrice = (p) => new Intl.NumberFormat(lang, { style: "currency", currency }).format(p);

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!form.company_name.trim()) { toast.error(t("exh_name_required")); return; }
    if (slot?.sm_roles?.length > 0 && !form.role) { toast.error(t("exh_choose_role")); return; }
    setLoading(true);
    try {
      const { data: exh } = await api.post(`/events/${event.id}/agenda/${slot.id}/exhibitors`, form);
      if (isPaid) {
        // Trigger Stripe Checkout
        const origin = window.location.origin;
        const { data } = await api.post(`/events/${event.id}/exhibitors/${exh.id}/checkout`, { origin_url: origin });
        window.location.href = data.checkout_url;
        return;
      }
      toast.success(user.role === "admin" ? t("exh_validated") : t("exh_submitted"));
      onRegistered?.(exh);
      onClose?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="exh-register-modal">
      <button onClick={onClose} className="absolute inset-0 bg-neutral-900/70 backdrop-blur-sm" aria-label={t("close")} />
      <div className="relative max-h-[90vh] w-full max-w-lg overflow-hidden rounded-3xl bg-white shadow-2xl flex flex-col">
        <div className="flex items-center gap-3 border-b border-neutral-100 px-6 py-4">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-wine-100"><Building2 className="h-5 w-5 text-wine-600" /></div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-bold text-neutral-900">{t("exh_title")}</h2>
            <p className="text-xs text-neutral-500 truncate">{slot?.title}</p>
          </div>
          <button onClick={onClose} className="grid h-9 w-9 place-items-center rounded-full text-neutral-500 hover:bg-neutral-100"><X className="h-5 w-5" /></button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
          {myCompany && (
            <div className="flex items-center gap-3 rounded-2xl border border-wine-100 bg-wine-50/60 px-3 py-2.5" data-testid="exh-prefill-banner">
              {form.company_logo ? (
                <img src={form.company_logo} alt="" className="h-10 w-10 rounded-lg bg-white object-contain p-1" />
              ) : (
                <div className="grid h-10 w-10 place-items-center rounded-lg bg-white"><Crown className="h-4 w-4 text-wine-600" /></div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-wider text-wine-700">{t("exh_company_card")}</div>
                <div className="truncate text-sm font-semibold text-neutral-900">{myCompany.commercial_name || myCompany.name}</div>
              </div>
              <button
                type="button"
                onClick={resync}
                disabled={resyncing}
                className="inline-flex items-center gap-1.5 rounded-full border border-wine-300 bg-white px-3 py-1.5 text-[11px] font-bold text-wine-700 hover:bg-wine-50 disabled:opacity-50"
                data-testid="exh-resync"
                title={t("exh_resync_title")}
              >
                {resyncing ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                {t("exh_resync")}
              </button>
            </div>
          )}

          <div>
            <label className="text-xs font-semibold text-neutral-700">{t("exh_company_name")}</label>
            <input value={form.company_name} onChange={(e) => update({ company_name: e.target.value })}
                   className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" required
                   data-testid="exh-company-name" />
          </div>

          {slot?.sm_roles?.length > 0 && (
            <div>
              <label className="text-xs font-semibold text-neutral-700">{t("exh_role")}</label>
              <select value={form.role} onChange={(e) => update({ role: e.target.value })}
                      className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" required
                      data-testid="exh-role">
                <option value="">{t("exh_select")}</option>
                {slot.sm_roles.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-neutral-700">{t("exh_booth")}</label>
              <input value={form.booth_number} onChange={(e) => update({ booth_number: e.target.value })}
                     className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" placeholder={t("exh_booth_ph")}
                     data-testid="exh-booth" />
            </div>
            <div>
              <label className="text-xs font-semibold text-neutral-700">{t("exh_logo")}</label>
              <input value={form.company_logo} onChange={(e) => update({ company_logo: e.target.value })}
                     className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" placeholder="https://…"
                     data-testid="exh-logo" />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-neutral-700">{t("exh_desc")}</label>
            <textarea rows={3} value={form.description} onChange={(e) => update({ description: e.target.value })}
                      className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm"
                      placeholder={t("exh_desc_ph")}
                      data-testid="exh-description" />
          </div>

          {/* Pricing card */}
          <div className={`rounded-2xl border p-4 ${isPaid ? "border-wine-200 bg-wine-50/40" : "border-emerald-200 bg-emerald-50/40"}`}>
            {isPaid ? (
              <div className="flex items-start gap-3">
                <CreditCard className="h-5 w-5 text-wine-600 shrink-0" />
                <div className="flex-1">
                  <div className="text-sm font-bold text-neutral-900">{t("exh_fee_title")}</div>
                  <div className="mt-0.5 text-2xl font-bold text-wine-700">{fmtPrice(price)}</div>
                  <p className="mt-1 text-xs text-neutral-600">{t("exh_secure_payment")}</p>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
                <div className="flex-1">
                  <div className="text-sm font-bold text-neutral-900">{t("exh_free_title")}</div>
                  <p className="mt-1 text-xs text-neutral-600">{user?.role === "admin" ? t("exh_auto_admin") : t("exh_review_team")}</p>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-full bg-neutral-100 px-5 py-2 text-xs font-semibold text-neutral-700 hover:bg-neutral-200" data-testid="exh-cancel">{t("cancel")}</button>
            <button type="submit" disabled={loading}
                    className="inline-flex items-center gap-2 rounded-full bg-wine-500 px-5 py-2 text-xs font-bold text-white hover:bg-wine-600 disabled:opacity-60"
                    data-testid="exh-submit">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {isPaid ? t("exh_pay").replace("{price}", fmtPrice(price)) : t("exh_confirm")}
            </button>
          </div>
          <GdprNotice compact />
        </form>
      </div>
    </div>
  );
}
